import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Briefcase, Rocket, KeyRound, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SparkLogo from '../components/SparkLogo';

const ROLE_STORAGE_KEY = 'getspark_pending_role';

const AuthPage = () => {
  const { signUpWithEmail, signInWithEmail, signInWithGoogle, resetPasswordForEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/app';

  const queryParams = new URLSearchParams(location.search);
  const initialMode = queryParams.get('signup') === '1' ? 'signup' : 'signin';
  const incomingRole = queryParams.get('role');

  const [mode, setMode] = useState(initialMode); // 'signin' | 'signup' | 'forgot'
  const [pendingRole, setPendingRole] = useState(() => {
    if (incomingRole === 'investor' || incomingRole === 'entrepreneur') return incomingRole;
    try {
      const saved = sessionStorage.getItem(ROLE_STORAGE_KEY);
      return saved === 'investor' || saved === 'entrepreneur' ? saved : null;
    } catch (_) {
      return null;
    }
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (incomingRole === 'investor' || incomingRole === 'entrepreneur') {
      try { sessionStorage.setItem(ROLE_STORAGE_KEY, incomingRole); } catch (_) {}
    }
  }, [incomingRole]);

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setSuccess('');
    setShowPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      if (mode === 'signin') {
        await signInWithEmail(email, password);
        navigate(from, { replace: true });
      } else if (mode === 'signup') {
        await signUpWithEmail(email, password, fullName);
        // Try auto-signin (works when Confirm email is OFF in Supabase Dashboard)
        try {
          await signInWithEmail(email, password);
          navigate(from, { replace: true });
        } catch (_) {
          setSuccess('Conta criada! Verifique seu e-mail para confirmar e depois faça login.');
          setMode('signin');
        }
      } else if (mode === 'forgot') {
        await resetPasswordForEmail(email);
        setSuccess('Enviamos um link de recuperação para seu e-mail. Confira sua caixa de entrada (e o spam).');
      }
    } catch (err) {
      setError(err.message || 'Algo deu errado.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true); setError('');
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err.message || 'Falha no login com Google.');
      setLoading(false);
    }
  };

  const title =
    mode === 'signup' ? 'Crie sua conta' :
    mode === 'forgot' ? 'Recuperar senha' :
    'Bem-vindo de volta';

  // Explicit resolution — one label per mode, no fallback surprises.
  let submitLabel;
  if (mode === 'signin') submitLabel = 'Entrar';
  else if (mode === 'signup') submitLabel = 'Criar conta';
  else submitLabel = 'Enviar link de recuperação';

  return (
    <div className="min-h-screen bg-[#050816] text-white flex items-center justify-center px-5 py-12 overflow-hidden relative">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(79,172,254,0.18), transparent 70%)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,167,38,0.18), transparent 70%)' }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <Link to="/" className="flex justify-center mb-6" data-testid="auth-logo-link">
          <SparkLogo height={72} />
        </Link>

        <div className="glass-card rounded-3xl p-7 sm:p-8" data-testid="auth-card">
          <div className="text-center mb-6">
            <h1 className="heading-display text-2xl font-bold text-white" data-testid="auth-title">{title}</h1>
            <p className="mt-1.5 text-xs text-slate-400">
              {mode === 'forgot'
                ? 'Informe seu e-mail e enviaremos um link para criar uma nova senha.'
                : 'Quando os dois se conectam, nasce uma Spark.'}
            </p>
          </div>

          {/* Role chip — appears only on signup when coming from a landing CTA */}
          {mode === 'signup' && pendingRole && (
            <div
              data-testid="auth-role-chip"
              className={`mb-5 rounded-xl border px-4 py-3 flex items-center gap-3 ${
                pendingRole === 'investor'
                  ? 'border-[#ffa726]/40 bg-[#ffa726]/[0.08]'
                  : 'border-[#4facfe]/40 bg-[#4facfe]/[0.08]'
              }`}
            >
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  pendingRole === 'investor' ? 'bg-[#ffa726]/20 text-[#ffd54f]' : 'bg-[#4facfe]/20 text-[#4facfe]'
                }`}
              >
                {pendingRole === 'investor' ? <Briefcase size={16} /> : <Rocket size={16} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Você está se cadastrando como</div>
                <div className="text-sm font-bold text-white truncate">
                  {pendingRole === 'investor' ? 'Investidor — quero apoiar negócios' : 'Empreendedor — tenho uma ideia'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPendingRole(null);
                  try { sessionStorage.removeItem(ROLE_STORAGE_KEY); } catch (_) {}
                }}
                className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-white transition-colors px-2 py-1 rounded-md hover:bg-white/[0.06]"
                data-testid="auth-role-chip-clear"
              >
                Trocar
              </button>
            </div>
          )}

          {/* Tabs (only 2 now) — hidden in forgot mode for cleaner UX */}
          {mode !== 'forgot' && (
            <div className="glass-card rounded-full p-1 grid grid-cols-2 mb-6 text-xs font-semibold" data-testid="auth-tabs">
              {[
                { id: 'signin', label: 'Entrar' },
                { id: 'signup', label: 'Criar conta' },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`auth-tab-${t.id}`}
                  onClick={() => switchMode(t.id)}
                  className={`px-3 py-2 rounded-full transition-all ${
                    mode === t.id ? 'bg-gradient-to-r from-[#4facfe] to-[#1e90ff] text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Google (hidden in forgot mode) */}
          {/* TEMPORARILY DISABLED — MVP focus: email/password auth only.
              Google OAuth implementation preserved (see handleGoogle, signInWithGoogle in AuthContext).
              To re-enable: change `false && mode !== 'forgot'` back to `mode !== 'forgot'`. */}
          {false && mode !== 'forgot' && (
            <>
              <button
                onClick={handleGoogle}
                disabled={loading}
                data-testid="auth-google"
                className="w-full glass-card rounded-xl py-3 text-sm font-semibold text-white hover:bg-white/[0.08] transition-all flex items-center justify-center gap-3 mb-4 disabled:opacity-60"
              >
                <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3c-2 1.5-4.5 2.5-7.3 2.5-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4 5.6l6.3 5.3C41.6 35.2 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/>
                </svg>
                Continuar com Google
              </button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
                <div className="relative flex justify-center"><span className="px-3 text-[10px] uppercase tracking-widest bg-[#050816] text-slate-500">ou</span></div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="auth-form">
            {mode === 'signup' && (
              <div className="relative">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text" required
                  data-testid="auth-input-name"
                  name="full_name"
                  autoComplete="name"
                  placeholder="Seu nome completo"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50"
                />
              </div>
            )}
            <div className="relative">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="email" required
                data-testid="auth-input-email"
                name="email"
                autoComplete="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50"
              />
            </div>
            {mode !== 'forgot' && (
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'} required minLength={6}
                  data-testid="auth-input-password"
                  name="password"
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  placeholder="Sua senha (mín. 6 caracteres)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-12 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="auth-toggle-password"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] active:bg-white/10 transition-colors touch-manipulation"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            )}

            {/* "Esqueci minha senha" — only on signin */}
            {mode === 'signin' && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => switchMode('forgot')}
                  data-testid="auth-forgot-link"
                  className="text-[11px] text-slate-400 hover:text-[#4facfe] transition-colors inline-flex items-center gap-1"
                >
                  <KeyRound size={11} /> Esqueci minha senha
                </button>
              </div>
            )}

            <AnimatePresence>
              {error && (
                <motion.div initial={{opacity:0, y:-5}} animate={{opacity:1,y:0}} exit={{opacity:0}} data-testid="auth-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                  {error}
                </motion.div>
              )}
              {success && (
                <motion.div initial={{opacity:0, y:-5}} animate={{opacity:1,y:0}} exit={{opacity:0}} data-testid="auth-success" className="text-xs text-green-300 bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
                  {success}
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="submit"
              disabled={loading}
              data-testid={`auth-submit-${mode}`}
              className={`w-full rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 ${
                mode === 'forgot' ? 'btn-gold' : 'btn-primary'
              }`}
            >
              {loading ? 'Aguarde...' : (
                <>
                  <span data-testid="auth-submit-label">{submitLabel}</span>
                  <ArrowRight size={14} />
                </>
              )}
            </button>
          </form>

          {mode === 'forgot' && (
            <button
              type="button"
              onClick={() => switchMode('signin')}
              data-testid="auth-back-to-signin"
              className="mt-4 w-full text-center text-xs text-slate-400 hover:text-white transition-colors"
            >
              ← Voltar para Entrar
            </button>
          )}

          <p className="mt-5 text-center text-[11px] text-slate-500">
            Ao continuar, você concorda com nossos termos e privacidade.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
